import * as HPKE from "hpke";
import { KEM_ML_KEM_1024 } from "@panva/hpke-noble";

export type HpkeAlgorithm = "p256" | "mlkem1024";
export const PQ_KEY_PREFIX = "mlkem1024:";
export const PQ_PLAINTEXT_PREFIX_LEN = 32;

export interface HpkePublicKey {
  key: HPKE.Key;
  fingerprint: string;
  algorithm: HpkeAlgorithm;
}

export const COMMITMENT_CONTEXT = new TextEncoder().encode(
  "credential-commitment"
);
export const COMMITMENT_LEN = 32;
export const SEAL_PROOF_CONTEXT = new TextEncoder().encode(
  "connector-secret-seal-v2"
);
export const EDIT_PROOF_CONTEXT = new TextEncoder().encode(
  "connector-destination-edit-v2"
);
export const PROOF_KEY_LEN = 32;
const FINGERPRINT_BYTES = 8;

export const suite = new HPKE.CipherSuite(
  HPKE.KEM_DHKEM_P256_HKDF_SHA256,
  HPKE.KDF_HKDF_SHA256,
  HPKE.AEAD_AES_256_GCM
);

export const pqSuite = new HPKE.CipherSuite(
  KEM_ML_KEM_1024,
  HPKE.KDF_HKDF_SHA384,
  HPKE.AEAD_AES_256_GCM
);

export function suiteForAlgorithm(algorithm: HpkeAlgorithm) {
  return algorithm === "mlkem1024" ? pqSuite : suite;
}

export function parseKeyEncoding(value: string) {
  const trimmed = value.trim();
  const algorithm: HpkeAlgorithm = trimmed.startsWith(PQ_KEY_PREFIX)
    ? "mlkem1024"
    : "p256";
  const raw = fromBase64(
    algorithm === "mlkem1024" ? trimmed.slice(PQ_KEY_PREFIX.length) : trimmed
  );
  return { algorithm, raw };
}

export function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function fromBase64(str: string): Uint8Array {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function computeFingerprint(data: Uint8Array): Promise<string> {
  const hash = new Uint8Array(
    await crypto.subtle.digest("SHA-256", Uint8Array.from(data))
  );
  return Array.from(hash.slice(0, FINGERPRINT_BYTES))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function parsePublicKey(encoded: string): Promise<HpkePublicKey> {
  const { algorithm, raw } = parseKeyEncoding(encoded);
  const selectedSuite = suiteForAlgorithm(algorithm);
  const key = await selectedSuite.DeserializePublicKey(raw);
  const serialized = await selectedSuite.SerializePublicKey(key);
  const fingerprint = await computeFingerprint(serialized);
  return { key, fingerprint, algorithm };
}

export async function hpkeSeal(
  plaintext: string,
  publicKey: HpkePublicKey,
  binding?: { scope: string; policy: string; issuedAt: number }
): Promise<{ ciphertext: string; editKey?: Uint8Array }> {
  const isPq = publicKey.algorithm === "mlkem1024";
  if (binding !== undefined && !isPq)
    throw new Error("Destination binding requires a post-quantum key");
  const selectedSuite = suiteForAlgorithm(publicKey.algorithm);
  const { encapsulatedSecret, ctx } = await selectedSuite.SetupSender(
    publicKey.key
  );
  const encoded = new TextEncoder().encode(plaintext);
  const payload = isPq
    ? new Uint8Array(PQ_PLAINTEXT_PREFIX_LEN + encoded.length)
    : encoded;
  if (isPq) payload.set(encoded, PQ_PLAINTEXT_PREFIX_LEN);
  const ct = await ctx.Seal(
    payload,
    binding === undefined ? undefined : new TextEncoder().encode(binding.scope)
  );
  const commitment = await ctx.Export(COMMITMENT_CONTEXT, COMMITMENT_LEN);
  if (binding !== undefined) {
    const core = `$hpke$2$${publicKey.fingerprint}$${toBase64(encapsulatedSecret)}$${toBase64(ct)}$${toBase64(commitment)}$${toBase64(new TextEncoder().encode(binding.scope))}`;
    const sealKey = new Uint8Array(
      await ctx.Export(SEAL_PROOF_CONTEXT, PROOF_KEY_LEN)
    );
    const editKey = new Uint8Array(
      await ctx.Export(EDIT_PROOF_CONTEXT, PROOF_KEY_LEN)
    );
    return {
      ciphertext: await sealSecretProof(
        core,
        binding.policy,
        binding.issuedAt,
        sealKey
      ),
      editKey,
    };
  }
  return {
    ciphertext: `$hpke$${isPq ? "2" : "1"}$${publicKey.fingerprint}$${toBase64(encapsulatedSecret)}$${toBase64(ct)}$${toBase64(commitment)}`,
  };
}

const encoder = new TextEncoder();

export function credentialScope(tenantId: string, field: string): string {
  return JSON.stringify({ tenantId, field });
}

async function signCredentialProof(
  core: string,
  policy: string,
  issuedAt: number,
  key: Uint8Array,
  kind: "seal" | "edit"
): Promise<string> {
  const policyB64 = toBase64(encoder.encode(policy));
  const message = `${core}$${policyB64}$${kind}$${issuedAt}`;
  const imported = await crypto.subtle.importKey(
    "raw",
    Uint8Array.from(key),
    { name: "HMAC", hash: "SHA-384" },
    false,
    ["sign"]
  );
  const proof = await crypto.subtle.sign(
    "HMAC",
    imported,
    encoder.encode(message)
  );
  return `${core}$${policyB64}$${kind}.${toBase64(proof)}$${issuedAt}`;
}

export async function sealSecretProof(
  core: string,
  policy: string,
  issuedAt: number,
  sealKey: Uint8Array
): Promise<string> {
  return signCredentialProof(core, policy, issuedAt, sealKey, "seal");
}

export async function sealDestinationProof(
  core: string,
  policy: string,
  issuedAt: number,
  editKey: Uint8Array
): Promise<string> {
  return signCredentialProof(core, policy, issuedAt, editKey, "edit");
}

export interface HpkeEnvelope {
  version: "1" | "2";
  fingerprint: string;
  enc: Uint8Array;
  ct: Uint8Array;
  commitment: Uint8Array;
  policy?: Uint8Array;
  scope?: Uint8Array;
  proof?: Uint8Array;
  proofKind?: "seal" | "edit";
  issuedAt?: number;
}

export function parseHpkeEnvelope(ciphertext: string): HpkeEnvelope {
  const parts = ciphertext.split("$");
  const version = parts[2];
  const bound = version === "2" && parts.length === 11;
  if (
    parts[0] !== "" ||
    parts[1] !== "hpke" ||
    !(
      (version === "1" && parts.length === 7) ||
      (version === "2" && (parts.length === 7 || bound))
    ) ||
    !/^[0-9a-f]{16}$/.test(parts[3] ?? "")
  )
    throw new Error("Invalid HPKE ciphertext format");
  const [
    ,
    ,
    ,
    fp,
    encB64,
    ctB64,
    commitB64,
    scopeB64,
    policyB64,
    proofWithKind,
    issuedAt,
  ] = parts;
  const parsedIssuedAt = bound ? Number(issuedAt) : undefined;
  const [proofKind, proofB64, extra] = proofWithKind?.split(".") ?? [];
  if (
    bound &&
    (!/^[1-9][0-9]*$/.test(issuedAt ?? "") ||
      !Number.isSafeInteger(parsedIssuedAt) ||
      parsedIssuedAt! <= 0 ||
      (proofKind !== "seal" && proofKind !== "edit") ||
      !proofB64 ||
      extra !== undefined)
  )
    throw new Error("Invalid HPKE ciphertext format");
  const envelope: HpkeEnvelope = {
    version: version as "1" | "2",
    fingerprint: fp!,
    enc: parseEnvelopeBase64(encB64!),
    ct: parseEnvelopeBase64(ctB64!),
    commitment: parseEnvelopeBase64(commitB64!),
    ...(bound && {
      scope: parseEnvelopeBase64(scopeB64!),
      policy: parseEnvelopeBase64(policyB64!),
      proof: parseEnvelopeBase64(proofB64!),
      proofKind: proofKind as "seal" | "edit",
      issuedAt: parsedIssuedAt,
    }),
  };
  if (
    envelope.enc.length !== (version === "2" ? 1568 : 65) ||
    (version === "1" && envelope.enc[0] !== 4) ||
    envelope.ct.length < (version === "2" ? 48 : 16) ||
    envelope.commitment.length !== COMMITMENT_LEN ||
    (bound &&
      (!envelope.scope?.length ||
        !envelope.policy?.length ||
        envelope.proof?.length !== 48))
  )
    throw new Error("Invalid HPKE ciphertext format");
  return envelope;
}

export function parseEnvelopeBase64(value: string): Uint8Array {
  const bytes = fromBase64(value);
  // Pony’s Base64 decoder requires canonical padding and rejects whitespace.
  if (toBase64(bytes) !== value)
    throw new Error("Invalid ciphertext Base64 encoding");
  return bytes;
}

export function isBoundHpkeEnvelope(value: string): boolean {
  return value.startsWith("$hpke$2$") && value.split("$").length === 11;
}
